import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Guid, ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import { getRouterLinkUrlById, ignoreNullish } from '@lsq-frontend/common-ui';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { BehaviorSubject } from 'rxjs';
import { first, map, pluck } from 'rxjs/operators';
import {
    GetBuyerImplementationAccountDetailsRequest,
    GetBuyerImplementationAccountDetailsResponse,
    GetBuyerImplementationAccountDetailsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-account-details';
import {
    UpdateBuyerImplementationCompanyInformationRequest,
    UpdateBuyerImplementationCompanyInformationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/update-buyer-implementation-company-information';
import {
    GetBuyerAccountDetailsRequest,
    GetBuyerAccountDetailsResponse,
    GetBuyerAccountDetailsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/get-buyer-account-details';
import {
    UpdateBuyerCompanyInformationRequest,
    UpdateBuyerCompanyInformationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/update-buyer-company-information';
import { ADMIN_BUYERS, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@Injectable({ providedIn: 'root' })
export class ManageBuyerService {
    private _buyerAccountDetailsSubject = new BehaviorSubject<
        GetBuyerAccountDetailsResponse | GetBuyerImplementationAccountDetailsResponse
    >(null);
    public buyerAccountDetails$ = this._buyerAccountDetailsSubject.asObservable();
    public buyerKey$ = this.buyerAccountDetails$.pipe(ignoreNullish(), pluck('buyerKey'));

    public isImplementation$ = this._activatedRoute.queryParams.pipe(
        map((queryParams) => queryParams.isImplementation === 'true')
    );

    constructor(
        private _getBuyerAccountDetailsService: GetBuyerAccountDetailsService,
        private _updateBuyerCompanyInformationService: UpdateBuyerCompanyInformationService,
        private _getBuyerImplementationAccountDetailsService: GetBuyerImplementationAccountDetailsService,
        private _updateBuyerImplementationCompanyInformationService: UpdateBuyerImplementationCompanyInformationService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _alertService: AlertService,
        private _errorToastBlacklistService: ErrorToastBlacklistService
    ) {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'operations/AdminManageBuyer/GetBuyerImplementationCompanyInformation',
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'operations/AdminManageBuyer/GetBuyerImplementationAccountDetails',
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'operations/AdminManageBuyer/GetBuyerCompanyInformation',
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'operations/AdminManageBuyer/GetBuyerAccountDetails',
            ResponseCode.Error
        );
    }

    public async loadBuyerAccountDetails(buyerKey: Guid): Promise<void> {
        try {
            this._buyerAccountDetailsSubject.next(null);
            this._buyerAccountDetailsSubject.next(
                await this._getBuyerAccountDetailsService.executeOnce(
                    new GetBuyerAccountDetailsRequest({
                        buyerKey
                    })
                )
            );
        } catch {
            this._handleLoadBuyerError();
        }
    }

    public async loadBuyerImplementationAccountDetails(
        buyerImplementationKey: Guid
    ): Promise<void> {
        try {
            this._buyerAccountDetailsSubject.next(null);
            this._buyerAccountDetailsSubject.next(
                await this._getBuyerImplementationAccountDetailsService.executeOnce(
                    new GetBuyerImplementationAccountDetailsRequest({
                        buyerImplementationKey
                    })
                )
            );
        } catch {
            this._handleLoadBuyerError();
        }
    }

    public async updateBuyerImplementationCompanyInformation(
        key: Guid,
        changedValues: any
    ): Promise<void> {
        const currentIsImplementation = await this.isImplementation$.pipe(first()).toPromise();

        if (currentIsImplementation) {
            await this._updateBuyerImplementationCompanyInformationService.executeOnce(
                new UpdateBuyerImplementationCompanyInformationRequest({
                    buyerImplementationKey: key,
                    ...changedValues
                })
            );
        } else {
            await this._updateBuyerCompanyInformationService.executeOnce(
                new UpdateBuyerCompanyInformationRequest({
                    buyerKey: key,
                    ...changedValues
                })
            );
        }
    }

    private _handleLoadBuyerError(): void {
        this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_BUYERS));
        this._alertService.error('Could not load buyer with provided key.');
    }
}
